import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Wallet, Copy, ExternalLink } from "lucide-react";

const money = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 2,
  }).format(Number(n || 0));

const badge = (s) => ({
  active: "bg-emerald-400/10 text-emerald-300",
  completed: "bg-blue-400/10 text-blue-300",
  pending: "bg-amber-400/10 text-amber-300",
  approved: "bg-emerald-400/10 text-emerald-300",
  rejected: "bg-red-400/10 text-red-300",
}[s] || "bg-white/5 text-white/50");

export default function CustomerMandates() {
  const [mandates, setMandates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [details, setDetails] = useState(null);
  const [amount, setAmount] = useState("");
  const [invoice, setInvoice] = useState(null);
  const [qrUrl, setQrUrl] = useState("");
  const [proof, setProof] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customer/mandates");
      setMandates(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load mandates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openDetails = async (m) => {
    setActive(m);
    setDetails(null);
    try {
      const { data } = await api.get(`/mandates/${m.mandate_id}`);
      setDetails(data);
      setAmount(String(data.paid_amount === 0 ? data.initial_amount : data.minimum_installment));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load mandate details");
    }
  };


  const submitUpi = async (e) => {
    e.preventDefault();
    if (!active) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/mandates/${active.mandate_id}/invoice`, {
        amount: Number(amount),
      });
      setInvoice(data);
      setProof(null);

      const qrPath = data.qr_url.replace(/^\/api/, "");
      const qr = await api.get(qrPath, { responseType: "blob" });
      if (qrUrl) URL.revokeObjectURL(qrUrl);
      setQrUrl(URL.createObjectURL(qr.data));

      toast.success("Installment invoice and UPI QR created");
      await openDetails(active);
      setInvoice(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create installment invoice");
    } finally {
      setBusy(false);
    }
  };

  const uploadProof = async (e) => {
    e.preventDefault();
    if (!active || !invoice || !proof) {
      toast.error("Select the payment screenshot first");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("screenshot", proof);
      form.append("note", note.trim());
      await api.post(
        `/mandates/${active.mandate_id}/installments/${invoice.installment_id}/manual`,
        form
      );
      toast.success("Screenshot submitted for admin review");
      setInvoice(null);
      setProof(null);
      setNote("");
      if (qrUrl) URL.revokeObjectURL(qrUrl);
      setQrUrl("");
      await openDetails(active);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not upload screenshot");
    } finally {
      setBusy(false);
    }
  };

  const downloadInvoice = async (mandateId, installmentId) => {
    try {
      const { data } = await api.get(
        `/mandates/${mandateId}/installments/${installmentId}/invoice`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${installmentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not download invoice");
    }
  };

  const payRazorpay = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/mandates/${active.mandate_id}/razorpay`, {
        amount: Number(amount),
      });
      if (data.payment_url) window.open(data.payment_url, "_blank", "noopener,noreferrer");
      else toast.error("Payment link was not returned");
      await openDetails(active);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create payment link");
    } finally {
      setBusy(false);
    }
  };

  const copy = async (value) => {
    try { await navigator.clipboard.writeText(value); toast.success("Copied"); }
    catch { toast.error("Could not copy"); }
  };

  const field = "w-full rounded-xl bg-black/40 border border-white/10 px-3 py-2.5 text-sm text-white outline-none focus:border-[#fbce18]/60";

  return (
    <div className="space-y-6 text-white" data-testid="customer-mandates-page">
      <div className="flex items-center justify-between gap-3">
        <div><h1 className="text-2xl font-semibold">My Mandates</h1><p className="mt-1 text-sm text-white/45">View your payment plans and make installments</p></div>
        <button onClick={load} className="rounded-xl border border-white/10 p-2.5"><RefreshCw size={17} className={loading?"animate-spin":""}/></button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {mandates.map(m=>(
          <button key={m.mandate_id} onClick={()=>openDetails(m)} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left transition hover:border-[#fbce18]/40">
            <div className="flex items-start justify-between gap-2">
              <div className="font-semibold">{m.title}</div>
              <span className={`rounded-full px-2 py-1 text-xs ${badge(m.status)}`}>{m.status}</span>
            </div>
            <div className="mt-1 text-xs text-white/40">{m.mandate_id}</div>
            <div className="mt-4 flex justify-between text-sm"><span className="text-white/50">Total</span><span>{money(m.total_amount)}</span></div>
            <div className="mt-1 flex justify-between text-sm"><span className="text-white/50">Paid</span><span className="text-emerald-300">{money(m.paid_amount)}</span></div>
            <div className="mt-1 flex justify-between text-sm"><span className="text-white/50">Remaining</span><span>{money(m.remaining_amount)}</span></div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-[#fbce18]" style={{width:`${Math.min(100,Number(m.progress_percent||0))}%`}}/></div>
            <div className="mt-2 text-xs text-white/40">{Number(m.progress_percent||0).toFixed(1)}% paid · View details</div>
          </button>
        ))}
        {!mandates.length && <div className="rounded-2xl border border-white/10 p-6 text-sm text-white/40">{loading?"Loading mandates…":"No mandates assigned to your account yet."}</div>}
      </div>

      {active && details && (
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 md:p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div><h2 className="text-lg font-semibold">{details.title}</h2><p className="text-xs text-white/40">{details.mandate_id}</p></div>
            <button onClick={()=>{setActive(null);setDetails(null)}} className="text-sm text-white/50">Close</button>
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-black/25 p-3"><div className="text-xs text-white/45">Total</div><div className="mt-1 font-semibold">{money(details.total_amount)}</div></div>
            <div className="rounded-xl bg-black/25 p-3"><div className="text-xs text-white/45">Paid</div><div className="mt-1 font-semibold text-emerald-300">{money(details.paid_amount)}</div></div>
            <div className="rounded-xl bg-black/25 p-3"><div className="text-xs text-white/45">Remaining</div><div className="mt-1 font-semibold">{money(details.remaining_amount)}</div></div>
          </div>

          {details.status==="active" && details.remaining_amount>0 && (
            <div className="mt-5 grid gap-5 lg:grid-cols-2">
              <form onSubmit={submitUpi} className="space-y-3 rounded-xl border border-white/10 p-4">
                <h3 className="font-medium">Pay by UPI</h3>
                {details.allowed_methods?.includes("manual_upi") ? (
                  <>
                    <div className="rounded-lg bg-black/30 p-3 text-sm">
                      <div className="text-xs text-white/45">UPI ID</div>
                      <div className="mt-1 flex items-center justify-between gap-2">
                        <span className="break-all">{details.upi_id}</span>
                        <button type="button" onClick={()=>copy(details.upi_id)}><Copy size={15}/></button>
                      </div>
                    </div>
                    {!invoice ? (
                      <>
                        <label className="block text-xs text-white/50">Installment amount (₹)</label>
                        <input className={field} type="number"
                          min={details.paid_amount===0?details.initial_amount:details.minimum_installment}
                          max={details.remaining_amount} step="0.01" required
                          value={amount} onChange={e=>setAmount(e.target.value)}/>
                        <button disabled={busy} className="w-full rounded-xl bg-[#fbce18] px-4 py-2.5 text-sm font-semibold text-black disabled:opacity-50">
                          {busy ? "Creating invoice…" : "Generate invoice & UPI QR"}
                        </button>
                      </>
                    ) : (
                      <div className="space-y-3 rounded-lg bg-black/25 p-3">
                        <div className="text-sm font-medium">Invoice: {invoice.invoice_no}</div>
                        <div className="text-sm">Pay exactly <b>{money(invoice.amount)}</b></div>
                        {qrUrl && <img src={qrUrl} alt="UPI payment QR" className="mx-auto w-52 rounded-lg bg-white p-2"/>}
                        <button type="button"
                          onClick={() => window.open(
                            `/mandate-invoice/${active.mandate_id}/${invoice.installment_id}`,
                            "_blank",
                            "noopener,noreferrer"
                          )}
                          className="w-full rounded-xl border border-white/15 px-4 py-2.5 text-sm">
                          View Invoice
                        </button>
                        <div className="space-y-3">
                          <label className="block text-xs text-white/50">Payment screenshot (JPG, PNG or WEBP, max 5 MB)</label>
                          <input className={field} type="file" accept="image/jpeg,image/png,image/webp"
                            required onChange={e=>setProof(e.target.files?.[0] || null)}/>
                          <label className="block text-xs text-white/50">Note (optional)</label>
                          <textarea className={field} rows={2} value={note}
                            onChange={e=>setNote(e.target.value)} placeholder="Payment note"/>
                          <button type="button" onClick={uploadProof} disabled={busy || !proof}
                            className="w-full rounded-xl bg-[#fbce18] px-4 py-2.5 text-sm font-semibold text-black disabled:opacity-50">
                            {busy ? "Uploading…" : "Submit screenshot for review"}
                          </button>
                          <button type="button" onClick={()=>{setInvoice(null);setProof(null);setNote("");}}
                            className="w-full rounded-xl border border-white/10 px-4 py-2 text-sm">
                            Cancel / change amount
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : <p className="text-sm text-white/45">Manual UPI is not enabled for this mandate.</p>}
              </form>

              <div className="space-y-3 rounded-xl border border-white/10 p-4">
                <h3 className="font-medium">Pay with Razorpay</h3>
                {details.allowed_methods?.includes("razorpay") ? (
                  <>
                    <label className="block text-xs text-white/50">Installment amount (₹)</label>
                    <input className={field} type="number" min={details.paid_amount===0?details.initial_amount:details.minimum_installment} max={details.remaining_amount} step="0.01" value={amount} onChange={e=>setAmount(e.target.value)}/>
                    <p className="text-xs text-white/40">A secure Razorpay payment link will open in a new tab.</p>
                    <button disabled={busy} onClick={payRazorpay} className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#fbce18]/40 px-4 py-2.5 text-sm text-[#fbce18] disabled:opacity-50"><ExternalLink size={16}/> Create payment link</button>
                  </>
                ) : <p className="text-sm text-white/45">Razorpay is not enabled for this mandate.</p>}
              </div>
            </div>
          )}

          <div className="mt-5">
            <h3 className="mb-3 font-medium">Payment history</h3>
            <div className="space-y-2">
              {(details.installments||[]).map(p=>(
                <div key={p.installment_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-white/10 p-3 text-sm">
                  <div><div>{money(p.amount)} · {p.method}</div><div className="text-xs text-white/40">{p.installment_id}{p.invoice_no ? ` · ${p.invoice_no}` : ""}</div>{p.rejection_reason&&<div className="mt-1 text-xs text-red-300">Rejected: {p.rejection_reason}</div>}</div>
                  <div className="flex items-center gap-2">{p.method==="manual_upi" && p.invoice_no && <button
  onClick={() => window.open(
    `/mandate-invoice/${active.mandate_id}/${p.installment_id}`,
    "_blank",
    "noopener,noreferrer"
  )}
  className="text-xs text-[#fbce18]"
>
  View Invoice
</button>}<span className={`rounded-full px-2 py-1 text-xs ${badge(p.status)}`}>{p.status}</span></div>
                </div>
              ))}
              {!details.installments?.length && <p className="text-sm text-white/40">No payments submitted yet.</p>}
            </div>
          </div>
          {details.terms && <div className="mt-5 rounded-xl bg-black/25 p-3"><div className="text-xs text-white/45">Terms</div><p className="mt-1 whitespace-pre-wrap text-sm">{details.terms}</p></div>}
        </section>
      )}
    </div>
  );
}
